import React, { FC, useContext, useState } from 'react'
import { Box } from 'front-end/src/components'
import { AnalyticsHeader } from '../AnalyticsHeader'
import { t, Trans } from '@lingui/macro'
import BorderedBox from '../../../components/common/bordered-box/BorderedBox'
import { AnalyticsHighlight } from '../AnalyticsHighlight'
import AnalyticsChart from '../components/AnalyticsChart'
import { graphOptions } from '../chartOptions'
import { TimeUnitEnum } from '../../../graphql/private/PrivateSchemaTypes'
import { MediaOverviewTable } from './MediaOverviewTable'
import { ChartType } from 'chart.js'
import Loader from '../../../components/common/loader/Loader'
import { useMediaAnalyticsQuery } from './Media.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import { AnalyticsFiltersEnum } from '../constants'

export const Media: FC = () => {
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const [chartTimeUnit, setChartTimeUnit] = useState<TimeUnitEnum>(
    TimeUnitEnum.DAY
  )

  const { data, loading } = useMediaAnalyticsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables
    }
  })

  if (loading || !data?.analytics.media) {
    return <Loader />
  }

  const media = data.analytics.media

  const { impressionsByTimeUnit, onlyEngagementsByTimeUnit } = media

  const renderAnalyticsBoxes = () => {
    return (
      <div className="AnalyticsRow">
        <BorderedBox className="AnalyticsRow__box">
          <div className="AnalyticsHighlight__row">
            <AnalyticsHighlight
              value={`${media.averageEngagement}%`}
              icon="rocket"
              description={t`Avg engagement`}
              iconColor="grey"
            />
            <AnalyticsHighlight
              value={media.playsCount}
              icon="control-play"
              description={t`Plays`}
              iconColor="green"
            />
            <AnalyticsHighlight
              value={media.impressionsCount}
              icon="eye"
              description={t`Impressions`}
              iconColor="blue"
            />
            <AnalyticsHighlight
              value={`${media.playRate}%`}
              icon="pie-chart"
              description={t`Play rate`}
              iconColor="grey"
            />
            <AnalyticsHighlight
              value={
                media.playTimeMinutes < 60
                  ? `${media.playTimeMinutes}m`
                  : `${(media.playTimeMinutes / 60).toFixed(1)}h`
              }
              icon="clock"
              description={t`Play time`}
              iconColor="green"
            />
          </div>

          <AnalyticsChart
            width={800}
            height={160}
            config={{
              type: 'line' as ChartType,
              options: graphOptions
            }}
            data={[
              {
                dataset: impressionsByTimeUnit,
                color: '11,146,213',
                tooltipLabel: t`Impressions`
              },
              { dataset: onlyEngagementsByTimeUnit, tooltipLabel: t`Plays` }
            ]}
            onGranularityChanged={setChartTimeUnit}
            granularity={chartTimeUnit}
          />
        </BorderedBox>
      </div>
    )
  }

  return (
    <Box>
      <AnalyticsHeader
        showFilterPanel
        activeFilters={[
          AnalyticsFiltersEnum.DATE_FILTER,
          AnalyticsFiltersEnum.INCLUDE_ADMINS,
          AnalyticsFiltersEnum.INCLUDE_ARCHIVED,
          AnalyticsFiltersEnum.WORKSPACE_FILTER
        ]}
      >
        <Trans>Media</Trans>
      </AnalyticsHeader>

      {renderAnalyticsBoxes()}
      <MediaOverviewTable />
    </Box>
  )
}
